// ============================================================
// dndPathHelpers.js
// SafetyCulture-style global tree DnD path utilities
// ============================================================

// Converts "droppable-root.items.0.items" → ["root", "items", "0", "items"]
export function normalizePath(pathString) {
  if (!pathString) return ["root"];
  return pathString
    .replace(/^droppable-/, "") // remove prefix
    .split(".")
    .filter(Boolean);
}

// Given a sections array and a path, return the correct items[] array.
export function getItemsByPath(sections, pathString) {
  const path = normalizePath(pathString);

  // root → top-level sections
  if (path.length === 1 && path[0] === "root") return sections;

  let current = sections;

  for (let i = 1; i < path.length; i++) {
    const key = path[i];

    if (!isNaN(Number(key))) {
      // numeric index: go into items[]
      const idx = Number(key);
      if (!current[idx]) return [];
      current = current[idx].items || [];
    }
  }
  return current;
}

// Resolve which *section* owns a droppable container.
export function getParentSectionIdFromPath(sections, pathString) {
  const path = normalizePath(pathString);

  if (path.length === 1 && path[0] === "root") return null;

  let current = sections;
  let sectionId = null;

  for (let i = 1; i < path.length; i++) {
    const key = path[i];

    if (!isNaN(Number(key))) {
      const idx = Number(key);
      const item = current[idx];
      if (!item) return null;

      if (item.type === "section") sectionId = item.id;

      current = item.items || [];
    }
  }

  return sectionId;
}

// Build a droppable path for children of a given path.
export function buildDroppablePath(parentPath, index) {
  const base = parentPath.replace(/^droppable-/, "");
  if (index === null || index === undefined) {
    return `droppable-${base}`;
  }
  return `droppable-${base}.items.${index}.items`;
}

// ============================================================
// NESTED DND HELPERS — FULL SAFETYCULTURE-STYLE GLOBAL DND
// Path format example: "sections.0.items.3.items.1"
// ============================================================

// Return the item located at path + index
export function getItemAtPath(sections, pathString, index) {
  const path = pathString.split(".").filter(Boolean);
  let current = sections;

  for (let i = 1; i < path.length; i++) {
    const key = path[i];

    if (!isNaN(Number(key))) {
      const idx = Number(key);
      if (!current[idx]) return null;
      current = current[idx].items || [];
    }
  }

  return current[index] || null;
}

// Remove an item immutably
export function removeItemAtPath(sections, pathString, index) {
  const path = pathString.split(".").filter(Boolean);

  function recurse(list, depth = 0) {
    if (depth === path.length - 1) {
      return list.filter((_, i) => i !== index);
    }

    const key = path[depth];
    if (!isNaN(Number(key))) {
      const idx = Number(key);
      return list.map((item, i) =>
        i === idx
          ? { ...item, items: recurse(item.items || [], depth + 1) }
          : item
      );
    }

    return list;
  }

  return recurse(sections);
}

// Insert an item immutably
export function insertItemAtPath(sections, pathString, index, item) {
  const path = pathString.split(".").filter(Boolean);

  function recurse(list, depth = 0) {
    if (depth === path.length - 1) {
      const newList = [...list];
      newList.splice(index, 0, item);
      return newList;
    }

    const key = path[depth];
    if (!isNaN(Number(key))) {
      const idx = Number(key);
      return list.map((child, i) =>
        i === idx
          ? { ...child, items: recurse(child.items || [], depth + 1) }
          : child
      );
    }

    return list;
  }

  return recurse(sections);
}

// Retrieve a section object at a given path.
export function getSectionAtPath(sections, pathString) {
  const path = normalizePath(pathString);

  if (path.length === 1 && path[0] === "root") return null;

  let current = sections;
  let lastSection = null;

  for (let i = 1; i < path.length; i++) {
    const key = path[i];

    if (!isNaN(Number(key))) {
      const idx = Number(key);
      const item = current[idx];
      if (!item) break;

      if (item.type === "section") lastSection = item;

      current = item.items || [];
    }
  }

  return lastSection;
}
